import { LocalBusinessJsonLd, BreadcrumbJsonLd } from 'next-seo';
import { makeLocalBusinessJsonLd } from '@utils/jsonLd/business';
import { getCityStateLocationData } from '@scenes/LocationPage/locationPage.utils';
import { IHash } from 'types/base.types';
import { substituteMappedValues } from '@integrations/contentful/contentful.utils';
import { GeoCoordinates, LocationData, LocationDataCity, LocationDataState, LocationPageProps, SkuData, Stats } from './locationPage.types';

type LocationPageSkuSeoProps = {
  locationData: LocationDataState | LocationDataCity;
  skuData: SkuData;
  ratingAttributes?: LocationPageProps['ratingAttributes'];
  geoCoordinates: GeoCoordinates;
};

type LocationPageGenericSeoProps = {
  locationData: LocationData;
  stats: Stats;
  geoCoordinates: GeoCoordinates;
};

export const LocationPageSkuSeo = ({ locationData, skuData, ratingAttributes, geoCoordinates }: LocationPageSkuSeoProps) => {
  const { city, state } = getCityStateLocationData(locationData);

  const mapper: IHash<string> = { city, state };

  const defaultName = locationData.displayName as string;
  const name = city || state || defaultName;
  const destination = substituteMappedValues(`{{city,state}}`, mapper) || defaultName;
  const description = `Order a ${skuData.name} from HelloTech ${destination}`;
  const location = destination;
  const rating = ratingAttributes;
  let geoCoords = {};

  if (geoCoordinates) {
    const [lat, long] = geoCoordinates;
    geoCoords = { geo: { latitude: lat, longitude: long } };
  }

  const items = [];

  if (locationData.type === 'city') {
    const { state: s, url, displayName } = locationData as LocationDataCity;
    items.push(
      { name: 'United States', item: 'https://www.hellotech.com/us' },
      { name: s.displayName!, item: `https://www.hellotech.com${s.url}` },
      { name: displayName!, item: `https://www.hellotech.com${url}` },
    );
  }

  if (locationData.type === 'state') {
    items.push(
      { name: 'United States', item: 'https://www.hellotech.com/us' },
      { name: locationData.displayName!, item: `https://www.hellotech.com${locationData.url}` },
    );
  }

  if (locationData.type === 'country') {
    items.push(
      { name: 'HelloTech', item: 'https://www.hellotech.com' },
      { name: locationData.displayName!, item: `https://www.hellotech.com${locationData.url}` },
    );
  }

  return (
    <>
      <LocalBusinessJsonLd
        {...geoCoords}
        priceRange={skuData.startsAtPrice}
        {...makeLocalBusinessJsonLd({ skuData, rating, name, description, location })}
      />
      <BreadcrumbJsonLd items={items} />
    </>
  );
};

export const LocationPageGenericSeo = ({ locationData, stats, geoCoordinates }: LocationPageGenericSeoProps) => {
  const { city, state } = getCityStateLocationData(locationData);

  const mapper: IHash<string> = { city, state };

  const defaultName = locationData.displayName as string;
  const name = city || state || defaultName;
  const destination = substituteMappedValues(`{{city,state}}`, mapper) || defaultName;
  const description = `Best Tech Support in ${destination} Near Me`;
  const location = destination;
  const rating = { rating: stats?.stats?.averageReviewsCount || 4.8, ratingCount: stats?.stats?.completedServicesCount || 1120349 };

  let geoCoords = {};

  if (geoCoordinates) {
    const [lat, long] = geoCoordinates;
    geoCoords = { geo: { latitude: lat, longitude: long } };
  }

  const items = [];

  if (locationData.type === 'city') {
    const { state: s, url, displayName } = locationData as LocationDataCity;
    items.push(
      { name: 'United States', item: 'https://www.hellotech.com/us' },
      { name: s.displayName!, item: `https://www.hellotech.com${s.url}` },
      { name: displayName!, item: `https://www.hellotech.com${url}` },
    );
  }

  if (locationData.type === 'state') {
    items.push(
      { name: 'HelloTech', item: 'https://www.hellotech.com' },
      { name: 'United States', item: 'https://www.hellotech.com/us' },
      { name: locationData.displayName!, item: `https://www.hellotech.com${locationData.url}` },
    );
  }

  if (locationData.type === 'country') {
    items.push(
      { name: 'HelloTech', item: 'https://www.hellotech.com' },
      { name: locationData.displayName!, item: `https://www.hellotech.com${locationData.url}` },
    );
  }

  return (
    <>
      <LocalBusinessJsonLd {...geoCoords} priceRange="$" {...makeLocalBusinessJsonLd({ rating, name, description, location })} />
      <BreadcrumbJsonLd items={items} />
    </>
  );
};
